import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  PersonAdd as AssignIcon,
  Assignment as ViewAssignmentsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { listModules, deleteModule, Module } from '../../services/lessonService';
import AssignLessonDialog from './AssignLessonDialog';
import ViewAssignmentsDialog from './ViewAssignmentsDialog';

export const LessonsList: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<Module | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [moduleToAssign, setModuleToAssign] = useState<Module | null>(null);
  const [viewAssignmentsDialogOpen, setViewAssignmentsDialogOpen] = useState(false);
  const [moduleToViewAssignments, setModuleToViewAssignments] = useState<Module | null>(null);

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const result = await listModules(50); // Load up to 50 modules
      setModules(result.modules);
    } catch (err: any) {
      setError(err.message || 'Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'published':
        return 'success';
      case 'archived':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDeleteClick = (e: React.MouseEvent, module: Module) => {
    e.stopPropagation(); // Prevent card click
    setModuleToDelete(module);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!moduleToDelete) return;

    try {
      setDeleting(true);
      await deleteModule(moduleToDelete.module_id);
      setDeleteDialogOpen(false);
      setModuleToDelete(null);
      await loadModules(); // Reload the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete module');
    } finally {
      setDeleting(false);
    }
  };

  const handleAssignClick = (e: React.MouseEvent, module: Module) => {
    e.stopPropagation(); // Prevent card click
    setModuleToAssign(module);
    setAssignDialogOpen(true);
  };

  const handleAssignSuccess = () => {
    setAssignDialogOpen(false);
    setModuleToAssign(null);
  };

  const handleViewAssignmentsClick = (e: React.MouseEvent, module: Module) => {
    e.stopPropagation(); // Prevent card click
    setModuleToViewAssignments(module);
    setViewAssignmentsDialogOpen(true);
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">My Lessons</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/teacher/create-lesson')}
        >
          Create New Lesson
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Lessons Grid */}
      {modules.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No lessons yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first lesson to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/teacher/create-lesson')}
            >
              Create First Lesson
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {modules.map((module) => (
            <Grid item xs={12} sm={6} md={4} key={module.module_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
                onClick={() => {
                  if (module.status === 'published') {
                    navigate(`/teacher/lessons/${module.module_id}/view`);
                  } else {
                    navigate(`/teacher/lessons/${module.module_id}/units`);
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={module.status}
                        color={getStatusColor(module.status) as any}
                        size="small"
                      />
                      <Chip label={module.subject} size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {module.status === 'published' && (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => handleViewAssignmentsClick(e, module)}
                            title="View Assignments"
                            color="info"
                          >
                            <ViewAssignmentsIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleAssignClick(e, module)}
                            title="Assign to Students"
                            color="success"
                          >
                            <AssignIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/teacher/lessons/${module.module_id}/units`);
                            }}
                            title="Edit Units"
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/teacher/lessons/${module.module_id}/view`);
                            }}
                            title="Preview Lesson"
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {module.status === 'draft' && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, module)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {module.title}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Grade {module.grade_level} • {module.duration_hours} hours
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {module.unit_count} {module.unit_count === 1 ? 'unit' : 'units'}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 2, display: 'block' }}
                  >
                    Created {formatDate(module.created_at)}
                  </Typography>

                  {module.learning_objectives.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Learning Objectives:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {module.learning_objectives[0]}
                        {module.learning_objectives.length > 1 && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}
                            +{module.learning_objectives.length - 1} more
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Lesson?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{moduleToDelete?.title}"? This action cannot be undone.
            All units and content will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Lesson Dialog */}
      {moduleToAssign && (
        <AssignLessonDialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          moduleId={moduleToAssign.module_id}
          moduleTitle={moduleToAssign.title}
          onAssignSuccess={handleAssignSuccess}
        />
      )}

      {/* View Assignments Dialog */}
      {moduleToViewAssignments && (
        <ViewAssignmentsDialog
          open={viewAssignmentsDialogOpen}
          onClose={() => setViewAssignmentsDialogOpen(false)}
          moduleId={moduleToViewAssignments.module_id}
          moduleTitle={moduleToViewAssignments.title}
        />
      )}
    </Box>
  );
};
